import './LeaderboardChart.css';

// Define the shape of a user object
interface User {
  id: string | number;
  name: string;
  avatar: string;
  value: number;
  nc_week: number;
  category: string;
}

// Define the props for the LeaderboardChart component
interface LeaderboardChartProps {
  data: User[];
  objective: number;
}

const LeaderboardChart: React.FC<LeaderboardChartProps> = ({ data, objective }) => {

  // Find top performer (only one with highest percentage >= 95)
  const topPerformer: User | null = data.reduce<User | null>((top, user) => {
    const percentage = (user.value / objective) * 100;
    if (percentage >= 95) {
      if (!top || user.value > top.value) {
        return user;
      }
    }
    return top;
  }, null);

  // Group data by performance ranges and sort by value (descending)
  const ncWeekUsers = data
    .filter(user => user.nc_week > 0)
    .sort((a, b) => b.value - a.value);

  const below80 = data
    .filter(user => {
      const percentage = (user.value / objective) * 100;
      return percentage < 85;
    })
    .sort((a, b) => b.value - a.value);

  const step1 = data
    .filter(user => {
      const percentage = (user.value / objective) * 100;
      return percentage >= 85 && percentage < 95;
    })
    .sort((a, b) => b.value - a.value);

  const step2 = data
    .filter(user => {
      const percentage = (user.value / objective) * 100;
      return percentage >= 95  && user !== topPerformer;
    })
    .sort((a, b) => b.value - a.value);

  // Add top performer to step2 (if exists) at the start
  if (topPerformer) {
    step2.unshift(topPerformer);
  }


  // Render user cards for a group
  const renderUserGroup = (users: User[]) => {
    return users.map((user, index) => {
      const performancePercentage = Math.min((user.value / objective) * 100, 100);
      
      const avatarColor =
        user.nc_week > 0 ? 'red' :
        user === topPerformer ? 'gold' :
        performancePercentage >= 95 ? 'green' :
        performancePercentage >= 85 ? 'orange' :
        user.category === 'commercial' ? 'purple' :
        user.category === 'sourcing' ? 'lightblue' :
        user.category === 'relance' ? 'blue' :
        user.name === 'Meriem Rahib' ? 'commercialManager' :
        user.name === 'Hafid Elhouaf' ? 'sourcingManager' : 'relanceManager';

      return (
        <div
          key={user.id}
          className={`racer-card bg-white/10 backdrop-blur-md shadow-xl hover:scale-105 transition-transform duration-300 ${user === topPerformer ? 'top-performer' : ''}`}
          data-animate={index}
        >
          <div className="racer-info flex-1 p-2">
            <div className="flex items-center justify-between">
              {user === topPerformer && (
                <span className="badge bg-yellow-500 text-black px-2 py-1 rounded-full text-xs font-semibold animate-pulse">
                  Top Performer
                </span>
              )}
              {user.nc_week > 0 && (
                <span className="badge bg-red-500 text-white px-3 py-1 rounded-full text-xs font-semibold animate-pulse">
                  NC:  {user.nc_week}
                </span>
              )}
            </div>
            <div className="racer-value mt-1">
              <img
              src={user.avatar}
              alt={user.name}
              className={`avatar ${avatarColor} w-10 h-10 rounded-full border-3 transition-all duration-300`}
            />
              <span className="centered-text text-lg font-bold">{user.value}%</span>
              <div className="progress-bar bg-gray-700 h-2 rounded-full mt-1">
                <div
                  className={`h-full rounded-full ${avatarColor}-progress`}
                  style={{ width: `${performancePercentage}%` }}
                ></div>
              </div>
            </div>
          </div>
        </div>
      );
    });
  };

  return (
    <div className="race-chart-container">
      <div className="race-track">
        {/* NC Week area */}
        <div className="threshold-line nc-week-area">
          <span className="threshold-label bg-red-500 text-white"> (NC) 😠مشوه بنآ </span>
          <div className="grid-container">
            {renderUserGroup(ncWeekUsers)}
          </div>
        </div>
        
        {/* Below 80% area */}
        <div className="threshold-line below80-area">
          <span className="threshold-label bg-gray-500 text-white"> ( {'< 85%'} ) 😕مفضي</span>
          <div className="grid-container">
            {renderUserGroup(below80)} 
          </div>
        </div>

        {/* Step 1 area (80-94%) */}
        <div className="threshold-line step1-area">
          <span className="threshold-label bg-orange-500 text-white">{'( < 95% )'} 😏يلآه حللتي رزقك</span>
          <div className="grid-container">
            {renderUserGroup(step1)}
          </div>
        </div>

        {/* Step 2 area (95-100%) */}
        <div className="threshold-line step2-area">
          <span className="threshold-label bg-green-500 text-white"> وآآآعر 🥳(95% - 100%)</span>
          <div className="grid-container">
            {renderUserGroup(step2)}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LeaderboardChart;